import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { trackEvent } from '../../../analytics';
import { 
  createRaiseImpactCopy, 
  createKeepPerPoundCopy, 
  formatCurrency 
} from '../domain/copyHelpers';

export const PercentageRaisePanel = ({ 
  currentSalary, 
  onCalculate, 
  results, 
  loading = false, 
  error 
}) => {
  const [raisePct, setRaisePct] = useState('');
  const [validationError, setValidationError] = useState('');

  const validateRaisePercentage = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return 'Raise percentage must be between 0 and 100';
    }
    return '';
  };

  const handleRaiseChange = (event) => {
    const value = event.target.value;
    setRaisePct(value);
    
    if (value.trim() === '') {
      setValidationError('');
      return;
    }
    
    const error = validateRaisePercentage(value);
    setValidationError(error);
  };

  const handleCalculate = () => {
    const numValue = parseFloat(raisePct);
    
    if (isNaN(numValue)) {
      setValidationError('Please enter a valid percentage');
      return;
    }

    const validationError = validateRaisePercentage(numValue);
    if (validationError) {
      setValidationError(validationError);
      return;
    }

    setValidationError('');
    
    // Track analytics
    trackEvent('Pay Rise Simulator', 'Calculate Percentage Raise', `${numValue}%`);
    
    onCalculate({
      currentAnnualGross: currentSalary,
      raisePct: numValue
    });
  };

  const isFormValid = raisePct.trim() !== '' && 
                     !validationError && 
                     !validateRaisePercentage(raisePct);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Percentage Raise Calculator
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Enter your desired raise percentage to see the real after-tax impact.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <TextField
          label="Raise Percentage"
          value={raisePct}
          onChange={handleRaiseChange}
          type="number"
          inputProps={{ 
            min: 0, 
            max: 100, 
            step: 0.1,
            'aria-label': 'Raise percentage'
          }}
          placeholder="e.g. 10"
          disabled={loading}
          error={!!validationError}
          helperText={validationError}
          InputProps={{
            endAdornment: <Typography sx={{ color: 'text.secondary' }}>%</Typography>
          }}
          fullWidth
        />

        <Button
          variant="contained"
          onClick={handleCalculate}
          disabled={!isFormValid || loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Calculating...
            </>
          ) : (
            'Calculate Impact'
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {error}
        </Alert>
      )}

      {results && (
        <Box 
          role="region" 
          aria-live="polite" 
          aria-label="Calculation results"
          sx={{ 
            mt: 3, 
            p: 2, 
            border: 1, 
            borderColor: 'divider', 
            borderRadius: 1,
            bgcolor: 'grey.50'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Raise Impact Summary
          </Typography>

          {/* Main copy lines */}
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
            {createRaiseImpactCopy(parseFloat(raisePct), results.effectiveRaisePctNet)}
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            {createKeepPerPoundCopy(results.keepPerPound)}
          </Typography>

          {/* Detailed breakdown */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Current Salary
              </Typography>
              <Typography variant="body1">
                {formatCurrency(results.grossBefore)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                New Salary
              </Typography>
              <Typography variant="body1" sx={{ color: 'success.main' }}>
                {formatCurrency(results.grossAfter)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Current Take-Home
              </Typography>
              <Typography variant="body1">
                {formatCurrency(results.netBeforeAnnual)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                New Take-Home
              </Typography>
              <Typography variant="body1" sx={{ color: 'success.main' }}>
                {formatCurrency(results.netAfterAnnual)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Monthly Increase
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(results.deltaNetMonthly)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Annual Increase
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(results.deltaNetAnnual)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};